import React, { useState } from 'react';
import classes from './ProductDetails.module.css';
import { IProductDetail, ProductLongDescription } from '../../store/slices/productCategories';
import { ProductImages } from './ProductImages';
import orderProductIcon from '../../assets/icons/order-product.png';
import plusIcon from '../../assets/icons/plusIcon.svg';
import minusIcon from '../../assets/icons/minus.svg';
import { getListByLanguage, getTextByLanguage, removeInlineStyles } from '../../utils/miscUtils';
import { authorizeClient } from '../../networking/client';
import { AxiosResponse } from 'axios';

import testResults from '../../assets/images/test-results.png';
import PrimaryButtonV2 from '../UI/Buttons/PrimaryButtonV2';
import { Accordion } from '../UI/Accordion/Accordion';
import { useAppDispatch } from '../../store/store';
import { addCart } from '../../store/slices/cart';
import { useTranslation } from '../../hooks/useTranslations';

type Props = {
  product: IProductDetail;
  onContinueClick?: () => void;
  onCancelClick?: () => void;
};

interface ProductDiagnose {
  diagnose_id: number;
  diagnose_name: string;
  label: string;
  product_id: number;
  property_id: number;
  property_long_description: {
    text: string;
    language: string;
  }[];
  property_name: {
    text: string;
    language: string;
  }[];
  property_short_abbreviation: string;
  property_short_description: string;
  uom: string;
}
interface ProductDiagnosesResponse {
  product_diagnose_machine: {
    diagnose_machine_id: number;
    diagnose_machine_name: string;
  }[];
  product_diagnoses: ProductDiagnose[];
}

const ProductDetails: React.FC<Props> = ({ product, onContinueClick, onCancelClick }) => {
  const { translations } = useTranslation();
  const [selectedTabIdx, setSelectedTabIdx] = React.useState<number>(0);
  const [biomarkers, setBiomarkers] = React.useState<ProductDiagnose[]>([]);

  const [loading, setLoading] = React.useState<boolean>(true);
  const [productDescription, setProductDescription] = React.useState<ProductLongDescription[]>([]);
  const [productInstructions, setProductInstructions] = React.useState<ProductLongDescription[]>(
    []
  );

  const tabs = React.useMemo(() => {
    const description = product?.long_description ?? [];

    const instructions = product?.product_instruction ?? [];

    setProductDescription(getListByLanguage(description));
    setProductInstructions(getListByLanguage(instructions));

    let _tabs = [
      {
        idx: 0,
        name: 'product.information',
      },
      {
        idx: 1,
        name: 'test.results',
      },
      {
        idx: 2,
        name: 'instructions',
      },
    ];
    if (description.length === 0) {
      _tabs = _tabs.filter((tab) => tab.idx !== 0);
      setSelectedTabIdx(1);
    }
    if (instructions.length === 0) {
      _tabs = _tabs.filter((tab) => tab.idx !== 2);
    }
    return _tabs;
  }, [product]);

  const images = React.useMemo(() => {
    const _images =
      product.images?.filter(
        (image) => image.language === localStorage.getItem('lang') && image.source === 'web'
      ) ?? [];
    if (_images.length === 0) {
      return [{ url: orderProductIcon, merchant_product_image_id: -1 }];
    }
    return _images;
  }, [product]);

  React.useEffect(() => {
    authorizeClient
      .get(`/core/public/merchant/merchant_product_diagnoses/${product.product_id}`)
      .then((response: AxiosResponse<ProductDiagnosesResponse>) => {
        setBiomarkers(response.data.product_diagnoses);
      })
      .finally(() => setLoading(false));
  }, [product.product_id]);
  const dispatch = useAppDispatch();
  const [selectedLaboratoryLanguage] = useState<string>(
    product.languages && product.languages.length > 0 ? product.languages[0].join() : ''
  );
  const [quantity, setQuantity] = useState<number>(1);

  const quantityHandler = (operation: string) => {
    if (operation === 'increment') {
      setQuantity((prev) => prev + 1);
    } else {
      if (quantity === 1) return;
      setQuantity((prev) => prev - 1);
    }
  };
  return (
    <div className={classes.container}>
      <div className={classes.productDetails}>
        <ProductImages images={images} />

        <div className={classes.basicInfo}>
          <div className={classes.productName}>{getTextByLanguage(product.name)}</div>
          <div className={classes.productPrice}>
            {product.price ? `€ ${(product.price / 100).toFixed(2)}` : '€ 0.00'}
          </div>
          {product.claim && (
            <div
              dangerouslySetInnerHTML={{
                __html: removeInlineStyles(getTextByLanguage(product.claim)),
              }}
            />
          )}
          {product.product_group === 'H' ? (
            <div className={classes.addToCartContainer}>
              <div className={classes.quantityContainer}>
                <img
                  src={minusIcon}
                  onClick={() => quantityHandler('subtract')}
                  alt="minus"
                  className={classes.quantity}
                />
                <div className={classes.value}>{quantity}</div>
                <img
                  src={plusIcon}
                  onClick={() => quantityHandler('increment')}
                  alt="plus"
                  className={classes.quantity}
                />
              </div>
              <div
                className={classes.addToCartButton}
                onClick={() => {
                  dispatch(
                    addCart({
                      product: product,
                      total: 0,
                      laboratoryLanguage: selectedLaboratoryLanguage.split(','),
                      quantity: quantity,
                    })
                  );
                  // onCancelClick();
                }}
              >
                {translations('add.to.cart')}
              </div>
            </div>
          ) : (
            <div className={classes.buttonsContainer}>
              {/* <PrimaryButtonV2
                text={translations('continue')}
                buttonStyle={{ height: 40, borderRadius: 7, width: 160 }}
                onClick={() => onContinueClick()}
              /> */}
              {/* <PrimaryButtonV2
                text={translations('button.cancel')}
                invertedColors
                buttonStyle={{ borderWidth: 2, height: 40, borderRadius: 7, width: 160 }}
                containerStyle={{ marginLeft: 10 }}
                onClick={() => onCancelClick()}
              /> */}
            </div>
          )}
        </div>
      </div>
      {loading && (
        <div className={classes.biomarkersLoader}>{translations('loading.biomarkers')}</div>
      )}
      {biomarkers.length > 0 && (
        <div style={{ margin: '10px 0' }}>
          <Accordion
            header={
              <>
                {translations('biomarkers.header')}{' '}
                <span style={{ margin: '0 5px' }}>{biomarkers.length}</span>{' '}
                {translations('biomarker')}
              </>
            }
            bodyStyle={{ padding: 0 }}
          >
            {biomarkers.map((biomarker, index) => (
              <Accordion
                key={index}
                header={`${
                  biomarker.property_name?.length > 0
                    ? biomarker.property_name.find(
                        (bpn) => bpn.language === localStorage.getItem('lang')
                      )?.text ?? ''
                    : ''
                } (${biomarker.property_short_abbreviation})`}
                containerStyle={{ boxShadow: 'none' }}
                bodyStyle={{ paddingBottom: 20 }}
              >
                {biomarker.property_long_description &&
                  removeInlineStyles(getTextByLanguage(biomarker.property_long_description))}
              </Accordion>
            ))}
          </Accordion>
        </div>
      )}
      <div className={classes.productInfo}>
        <div className={classes.tabsContainer}>
          {tabs.map((tab) => (
            <div
              key={tab.idx}
              className={`${classes.tab} ${selectedTabIdx === tab.idx ? classes.selectedTab : ''}`}
              onClick={() => setSelectedTabIdx(tab.idx)}
            >
              {translations(tab.name)}
            </div>
          ))}
        </div>
        {selectedTabIdx === 0 &&
          productDescription.map((item, index) => (
            <Accordion header={item.title} key={index} containerStyle={{ boxShadow: 'none' }}>
              {item.text}
            </Accordion>
          ))}
        {selectedTabIdx === 1 && (
          <div className={classes.resultsContainer}>
            <img className={classes.resultsImage} src={testResults} alt={'test-results'} />
            <div className={classes.resultsText}>
              <p style={{ fontSize: 16 }}>{translations('test.results.header')}</p>
              <p>{translations('test.results.p1')}</p>
              <p>
                {translations('test.results.p2.1')}
                <br />
                {translations('test.results.p2.2')}
              </p>
              <p>
                {translations('test.results.p3.1')}
                <br />
                {translations('test.results.p3.2')}
              </p>
              <p>
                {translations('test.results.p4.1')}
                <br />
                {translations('test.results.p4.2')}
              </p>
              <PrimaryButtonV2
                text={translations('test.results.button')}
                buttonStyle={{
                  fontWeight: 600,
                  width: 240,
                  height: 50,
                  fontSize: 16,
                  marginRight: 'auto',
                  marginTop: 20,
                }}
                onClick={() => {
                  const documentId = `documents_id_${product.product_id}`;
                  const languageId = translations(documentId);
                  const allowedIds = {
                    EN: [
                      'documents_id_74',
                      'documents_id_96',
                      'documents_id_50',
                      'documents_id_77',
                      'documents_id_63',
                      'documents_id_84',
                      'documents_id_64',
                      'documents_id_78',
                      'documents_id_68',
                      'documents_id_79',
                      'documents_id_75',
                      'documents_id_95',
                      'documents_id_76',
                      'documents_id_80',
                      'documents_id_91',
                      'documents_id_92',
                      'documents_id_93',
                      'documents_id_90',
                      'documents_id_94',
                      'documents_id_65',
                      'documents_id_66',
                      'documents_id_67',
                    ],
                    DE: [
                      'documents_id_74',
                      'documents_id_96',
                      'documents_id_50',
                      'documents_id_77',
                      'documents_id_63',
                      'documents_id_84',
                      'documents_id_64',
                      'documents_id_78',
                      'documents_id_68',
                      'documents_id_79',
                      'documents_id_75',
                      'documents_id_95',
                      'documents_id_76',
                      'documents_id_80',
                      'documents_id_91',
                      'documents_id_92',
                      'documents_id_93',
                      'documents_id_66',
                      'documents_id_90',
                      'documents_id_94',
                      'documents_id_67',
                      'documents_id_65',
                    ],
                  };
                  const language = localStorage.getItem('lang') === 'en' ? 'EN' : 'DE';
                  const isIdAllowed = allowedIds[language]?.includes(documentId);

                  if (isIdAllowed) {
                    window.open(
                      `${process.env.REACT_APP_AWS_BUCKET}/documents/${language}/${languageId}.pdf`
                    );
                  } else {
                    window.open(
                      `${process.env.REACT_APP_AWS_BUCKET}/documents/Electrolyte+Test+Result.pdf`
                    );
                  }
                }}
              />
            </div>
          </div>
        )}
        {selectedTabIdx === 2 &&
          productInstructions.map((item, index) => (
            <Accordion header={item.title} key={index} containerStyle={{ boxShadow: 'none' }}>
              {item.text}
            </Accordion>
          ))}
      </div>
    </div>
  );
};

export { ProductDetails };
